import { getRandomInt } from "../Shared/Helpers.tsx";
import { generateProblemPages } from "../Shared/Generation/problemPages.ts";
import { latinDealer } from "../Shared/Generation/latinDealer.ts";
import { CoordinateGridOptions, CoordinateQuestionOptions, ProblemData } from "./Constants.ts";
import { effectiveGrid, gridRange } from "./CoordinatePlaneGenerator.ts";
import { greatestCommonDivisor } from "./FractionGenerator.ts";
import { ISettingsProps } from "./Models.ts";
import { PrintedLanguage, printedLanguageOf } from "./PrintedLanguage.ts";
import { coordinatePrompts as en } from "../Files/Prompts/en/coordinatePlane.ts";
import { coordinatePrompts as es } from "../Files/Prompts/es/coordinatePlane.ts";
import { coordinatePrompts as fr } from "../Files/Prompts/fr/coordinatePlane.ts";

/**
 * Slope from a graph and from two points (ADR 0126): two Coordinate Plane
 * question types that, unlike the rest, have no grid shared by a group of
 * questions. Each problem stands alone, so there is no `problemGroup` and the
 * page lays them out two to a row (`Problems-UI/Slope.tsx`).
 *
 * **The slopes.** Every slope is a whole number from 1 to 4 or a fraction p/q in
 * lowest terms with q from 2 to 4 and p from 1 to 4, positive or negative:
 * never 0, never vertical, never smaller than 1/4 or larger than 4 (`SLOPES`).
 *
 * **Dealing.** A page is dealt evenly in two directions at once, sign and type
 * (whole or fraction), by ADR 0122's Latin-square dealer (`Shared/Generation/
 * latinDealer.ts`): at 6 a page that is 3 up and 3 down, 3 whole and 3
 * fractions, which a fair shuffle of the four kinds cannot promise.
 *
 * **Built from the answer.** Both questions choose the slope first and place
 * the points after, because almost no pair of lattice points on these grids has
 * a slope in the set: a graph needs two points whose run is a multiple of q, and
 * a pair of points needs a rise and run that are a multiple of the slope's p and q.
 */

/** A slope p/q, in lowest terms: the sign is on `p`, `q` is positive. */
export interface Slope {
    p: number;
    q: number;
}

const WHOLE_SIZES = [1, 2, 3, 4];

/** The fractions p/q in lowest terms, q from 2 to 4 and p from 1 to 4, as positive slopes. */
const FRACTION_SLOPES: Slope[] = [2, 3, 4].flatMap((q) =>
    [1, 2, 3, 4].filter((p) => greatestCommonDivisor(p, q) === 1).map((p) => ({ p, q }))
);
const WHOLE_SLOPES: Slope[] = WHOLE_SIZES.map((p) => ({ p, q: 1 }));

/** Which of the four kinds a slope is: its sign, and whole or fraction. */
export interface SlopeKind {
    negative: boolean;
    fraction: boolean;
}

/** Every slope of a kind (ADR 0126 §2). */
export function slopesOfKind({ negative, fraction }: SlopeKind): Slope[] {
    const sizes = fraction ? FRACTION_SLOPES : WHOLE_SLOPES;
    return sizes.map(({ p, q }) => ({ p: negative ? -p : p, q }));
}

/** The kind of a slope. */
export function kindOf({ p, q }: Slope): SlopeKind {
    return { negative: p < 0, fraction: q !== 1 };
}

/** One page's kinds, even in sign and in type at once (ADR 0126 §2). */
export const slopeKindDealer = latinDealer([false, true], [false, true]);

/** A slope of the kind, drawn evenly from those the kind has. */
function drawSlope(kind: SlopeKind): Slope {
    const slopes = slopesOfKind(kind);
    return slopes[getRandomInt(0, slopes.length - 1)];
}

/**
 * A slope as plain text (ADR 0126 §5): the sign on the numerator, so the
 * denominator is positive; a denominator of 1 is not written. It does not reduce,
 * so `-4/6` prints as it is, and the ASCII hyphen is the only minus (ADR 0070).
 */
export function formatSlope(p: number, q: number): string {
    const numerator = q < 0 ? -p : p;
    const denominator = Math.abs(q);
    return denominator === 1 ? String(numerator) : `${numerator}/${denominator}`;
}

/**
 * The key of a Slope from Two Points question: the change in y over the change
 * in x, second point minus first. Unreduced first and then reduced when they
 * differ, and a whole number always shows its fraction first (`-6/2 = -3`,
 * `-3/1 = -3`); a fraction already in lowest terms is printed once.
 */
export function slopeKey(rise: number, run: number): string {
    const numerator = run < 0 ? -rise : rise;
    const denominator = Math.abs(run);
    const divisor = greatestCommonDivisor(numerator, denominator);
    const reduced = formatSlope(numerator / divisor, denominator / divisor);
    const unreduced = `${numerator}/${denominator}`;
    return unreduced === reduced ? reduced : `${unreduced} = ${reduced}`;
}

const PROMPTS: Record<PrintedLanguage, typeof en> = { en, es, fr };

/** The slope question types, which draw no shared grid. */
export function isSlopeQuestion(question: CoordinateQuestionOptions | undefined): boolean {
    return question === CoordinateQuestionOptions.SlopeFromGraph || question === CoordinateQuestionOptions.SlopeFromPoints;
}

/** The blank beside "slope =" on the sheet; the key replaces it with the answer. */
export const SLOPE_BLANK = "______";

type Point = { x: number; y: number };

// ─── slope from a graph ─────────────────────────────────────────────────────

/** The coordinates a marked point may take (ADR 0126 §3): off both axes, and clear of the outer lines. */
export function graphDotValues(grid: CoordinateGridOptions): number[] {
    return grid === CoordinateGridOptions.FourQuadrants ? [-5, -4, -3, -2, -1, 1, 2, 3, 4, 5] : [1, 2, 3, 4, 5, 6, 7, 8, 9];
}

/**
 * Every pair of marked points on a line of slope p/q: both on grid crossings
 * from `values`, a run that is a whole number of q's (so the rise is whole) and
 * at least two in each direction. The pair is ordered left to right.
 */
export function dotPairs(slope: Slope, values: number[]): [Point, Point][] {
    const pairs: [Point, Point][] = [];
    const inRange = new Set(values);
    for (const x1 of values) {
        for (const y1 of values) {
            for (let m = 1; m <= values.length; m++) {
                const x2 = x1 + slope.q * m;
                const y2 = y1 + slope.p * m;
                if (!inRange.has(x2) || !inRange.has(y2)) continue;
                if (x2 - x1 < 2 || Math.abs(y2 - y1) < 2) continue;
                pairs.push([{ x: x1, y: y1 }, { x: x2, y: y2 }]);
            }
        }
    }
    return pairs;
}

/** The line through a dot with slope p/q is y = (p/q) x + (q y - p x) / q: this is the numerator, a whole number. */
const interceptTop = (slope: Slope, dot: Point) => slope.q * dot.y - slope.p * dot.x;

function buildGraphProblem(kind: SlopeKind, grid: CoordinateGridOptions, language: PrintedLanguage, key: number): ProblemData | null {
    const slope = drawSlope(kind);
    const pairs = dotPairs(slope, graphDotValues(grid));
    if (pairs.length === 0) return null;
    const dots = pairs[getRandomInt(0, pairs.length - 1)];
    const { min, max } = gridRange(grid);
    return {
        key,
        numbers: [],
        operator: "",
        display: [`${PROMPTS[language].slopeWord} = ${SLOPE_BLANK}`, formatSlope(slope.p, slope.q)],
        coordinateQuestion: CoordinateQuestionOptions.SlopeFromGraph,
        slopeGraph: { min, max, slope, intercept: interceptTop(slope, dots[0]) / slope.q, dots },
    };
}

/** A line on a page is told apart by its slope and the numerator of its intercept. */
function graphFingerprint(problem: ProblemData): string {
    const { slope, dots } = problem.slopeGraph!;
    return `${slope.p}/${slope.q}:${interceptTop(slope, dots[0])}`;
}

// ─── slope from two points ──────────────────────────────────────────────────

/** The coordinates of a pair of points (ADR 0126 §4): -9 to 9 on four quadrants, 0 to 10 on the first. */
export function pointRange(grid: CoordinateGridOptions): { min: number; max: number } {
    return grid === CoordinateGridOptions.FourQuadrants ? { min: -9, max: 9 } : { min: 0, max: 10 };
}

/** The multiples k of a slope's p and q that fit the range, 1 up to 3. */
export function multiplesThatFit(slope: Slope, grid: CoordinateGridOptions): number[] {
    const { min, max } = pointRange(grid);
    const span = max - min;
    return [1, 2, 3].filter((k) => slope.q * k <= span && Math.abs(slope.p) * k <= span);
}

/** A pair's text, with no break inside it (the first sheets of Missing Corner broke "B (-2," from "5)"). */
const NO_BREAK_SPACE = String.fromCharCode(160);

function buildPointsProblem(
    kind: SlopeKind,
    grid: CoordinateGridOptions,
    language: PrintedLanguage,
    key: number,
    used: Set<string>
): ProblemData | null {
    const t = PROMPTS[language];
    const slope = drawSlope(kind);
    const fitting = multiplesThatFit(slope, grid);
    // About half the questions reduce: k is 1 for one in two, otherwise one of the larger multiples.
    const larger = fitting.filter((k) => k > 1);
    const k = larger.length > 0 && getRandomInt(0, 1) === 1 ? larger[getRandomInt(0, larger.length - 1)] : 1;
    // Either direction: the second point is up and right of the first, or down and left.
    const direction = getRandomInt(0, 1) === 0 ? 1 : -1;
    const run = direction * slope.q * k;
    const rise = direction * slope.p * k;

    const { min, max } = pointRange(grid);
    const starts: Point[] = [];
    const named = (point: Point) => `${point.x},${point.y}`;
    for (let x = min; x <= max; x++) {
        for (let y = min; y <= max; y++) {
            const second = { x: x + run, y: y + rise };
            if (second.x < min || second.x > max || second.y < min || second.y > max) continue;
            if (used.has(named({ x, y })) || used.has(named(second))) continue;
            starts.push({ x, y });
        }
    }
    // A page with every spot taken (it cannot be, at the counts offered) falls back to repeating a point.
    if (starts.length === 0) return null;
    const first = starts[getRandomInt(0, starts.length - 1)];
    const second = { x: first.x + run, y: first.y + rise };
    used.add(named(first));
    used.add(named(second));

    const written = (point: Point) => t.pair(point.x, point.y).replace(/ /g, NO_BREAK_SPACE);
    return {
        key,
        numbers: [],
        operator: "",
        display: [t.slopeFromPoints(written(first), written(second)), slopeKey(rise, run)],
        coordinateQuestion: CoordinateQuestionOptions.SlopeFromPoints,
    };
}

// ─── the sheet ──────────────────────────────────────────────────────────────

/**
 * A page-shaped set of slope problems (ADR 0126). Each page is dealt its own
 * kinds; the points on a Slope from Two Points page are all different, and no
 * line is drawn twice on a Slope from a Graph sheet.
 */
export function generateSlopePages(settings: ISettingsProps): ProblemData[][] | null {
    const grid = effectiveGrid(settings);
    const language = printedLanguageOf(settings);
    const fromGraph = settings.CoordinateQuestion === CoordinateQuestionOptions.SlopeFromGraph;
    const perPage = settings.NumberOfProblemsPerPage;
    const kinds: SlopeKind[] = [false, true].flatMap((negative) => [false, true].map((fraction) => ({ negative, fraction })));
    const deal = slopeKindDealer;
    // The points a page has used, by the page the problem is on.
    const usedOnPage = new Map<number, Set<string>>();

    return generateProblemPages<SlopeKind>({
        settings,
        choices: kinds,
        dealPage: (count) => deal(count).map(({ row, column }) => ({ negative: row, fraction: column })),
        build: (kind, key) => {
            if (fromGraph) return buildGraphProblem(kind, grid, language, key);
            const page = Math.floor((key - 1) / perPage);
            if (!usedOnPage.has(page)) usedOnPage.set(page, new Set());
            return buildPointsProblem(kind, grid, language, key, usedOnPage.get(page)!);
        },
        fingerprint: (problem) => (fromGraph ? graphFingerprint(problem) : problem.display![0]),
    });
}
